import React, { useState, useMemo } from 'react';
import { Phone, Mail, Paperclip, Edit, ExternalLink, FileText, Check, MessageSquare } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { Tooltip } from "reactstrap";
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Spin, Badge, Empty, Input, Button } from 'antd';
import toast from 'react-hot-toast';
import ContactLinksGenerator from "../../../utils/contactlinksgenerator";
import PopupComponent from "../../../components/PopupComponent";
import { getTemplates } from "../../../services/templates";
import { useAppDispatch } from "../../../hooks/hooks";
import { sendTemplate } from "../../../store/slices/messagesSlice";
import { Logger } from '../../../utils/logger';

interface LeadActionIconsProps {
  phone?: string;
  email?: string;
  onFileClick: () => void;
  onEditClick: (e: React.MouseEvent) => void;
  onFormResponseClick?: () => void;
  hasCustomForms?: boolean;
  contactLinksGenerator?: ContactLinksGenerator | null;
  leadId: string;
  leadName?: string;
}

interface Template {
  _id: string;
  name: string;
  status: string;
  category?: string;
  language?: string;
  components?: Array<{
    type: string;
    text?: string;
    format?: string;
  }>;
}

const LeadActionIcons: React.FC<LeadActionIconsProps> = ({
  phone,
  email,
  onFileClick,
  onEditClick,
  onFormResponseClick,
  hasCustomForms,
  leadId,
  leadName = "Unknown Lead",
  contactLinksGenerator,
}) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Get user details from Redux to check WhatsApp integration
  const userDetails = useSelector((state: any) => state?.rootReducers?.userData?.userDetails);

  // Check if WhatsApp Business API is integrated
  const isWhatsappIntegrated = useMemo(() => {
    const integrations = userDetails?.userIntegrations || [];
    const whatsappIntegration = integrations.find(
      (item: any) => item?.integration?.key === "whatsapp_business" && item?.isActive
    );
    return !!whatsappIntegration;
  }, [userDetails?.userIntegrations]);

  // Popup states
  const [showChoicePopup, setShowChoicePopup] = useState(false);
  const [showTemplatePopup, setShowTemplatePopup] = useState(false);

  // Template states
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<Template | null>(null);
  const [sendingTemplate, setSendingTemplate] = useState(false);
  const [templateSearchTerm, setTemplateSearchTerm] = useState("");

  // Memoize the WhatsApp link for better performance
  const whatsappLink = useMemo(() =>
    contactLinksGenerator?.whatsapp || '#',
    [contactLinksGenerator]
  );

  // Memoize the phone link
  const phoneLink = useMemo(() => `tel:+${phone}`, [phone]);

  // Memoize the email link
  const emailLink = useMemo(() => `mailto:${email}`, [email]);

  const [tooltipOpen, setTooltipOpen] = useState({
    phone: false,
    email: false,
    whatsapp: false,
    files: false,
    edit: false,
    external: false,
    quotation: false
  });

  type TooltipKey = 'phone' | 'email' | 'whatsapp' | 'files' | 'edit' | 'external' | 'quotation';

  const toggle = (name: TooltipKey) => {
    setTooltipOpen(prev => ({
      ...prev,
      [name]: !prev[name]
    }));
  };

  const isPhoneValid = phone && phone.length > 4;
  const isEmailValid = email && email.length > 8;

  const handleCreateQuotation = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/quotationlist', {
      state: {
        openCreateForm: true,
        leadForQuotation: {
          _id: leadId,
          name: leadName
        }
      }
    });
  };

  // Handle WhatsApp click - show popup if integrated, else open WhatsApp web
  const handleWhatsAppClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isPhoneValid) return;

    if (isWhatsappIntegrated) {
      // Show choice popup
      setShowChoicePopup(true);
    } else {
      // Open WhatsApp web directly
      window.open(whatsappLink, '_blank', 'noopener,noreferrer');
    }
  };

  // Handle choice: WhatsApp App
  const handleChooseWhatsAppApp = () => {
    setShowChoicePopup(false);
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
  };

  // Handle choice: WhatsApp API
  const handleChooseWhatsAppAPI = () => {
    setShowChoicePopup(false);
    setShowTemplatePopup(true);
    if (templates.length === 0) {
      fetchTemplatesList();
    }
  };

  // Fetch templates
  const fetchTemplatesList = async () => {
    setLoadingTemplates(true);
    try {
      Logger.log("Fetching WhatsApp templates for lead messaging...");
      const response = await getTemplates({
        page: 1,
        perPage: 100,
        isAscending: false
      });

      if (response.data?.data) {
        setTemplates(response.data.data);
        Logger.log("Templates loaded:", response.data.data.length);
      }
    } catch (err) {
      console.error("Failed to fetch templates:", err);
      toast.error("Failed to load templates");
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Send template
  const handleSendTemplate = async () => {
    if (!selectedTemplate || !phone) {
      toast.error("Please select a template");
      return;
    }

    if (selectedTemplate.status !== 'APPROVED') {
      toast.error(`Cannot send template: Status is ${selectedTemplate.status}`);
      return;
    }

    setSendingTemplate(true);
    try {
      Logger.log("Sending template to lead:", {
        phone: phone,
        templateId: selectedTemplate._id,
        templateName: selectedTemplate.name
      });

      await dispatch(sendTemplate({
        phone: phone,
        templateId: selectedTemplate._id,
      })).unwrap();

      toast.success(`Template sent to ${leadName}`);
      setShowTemplatePopup(false);
      setSelectedTemplate(null);
      setTemplateSearchTerm("");
    } catch (err: any) {
      console.error("Failed to send template:", err);
      toast.error(err?.message || "Failed to send template");
    } finally {
      setSendingTemplate(false);
    }
  };

  // Close template popup
  const handleCloseTemplatePopup = () => {
    setShowTemplatePopup(false);
    setSelectedTemplate(null);
    setTemplateSearchTerm("");
  };

  // Filter templates by search and only show approved ones first
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    if (templateSearchTerm.trim()) {
      const search = templateSearchTerm.toLowerCase();
      filtered = filtered.filter(t =>
        t.name?.toLowerCase().includes(search) ||
        t.category?.toLowerCase().includes(search)
      );
    }

    return filtered.sort((a, b) => {
      if (a.status === 'APPROVED' && b.status !== 'APPROVED') return -1;
      if (a.status !== 'APPROVED' && b.status === 'APPROVED') return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [templates, templateSearchTerm]);

  // Get template body text for preview
  const getTemplateBody = (template: Template): string => {
    const bodyComponent = template.components?.find(c => c.type === 'BODY');
    return bodyComponent?.text || 'No preview available';
  };

  // Render template item
  const renderTemplateItem = (template: Template) => {
    const isApproved = template.status === 'APPROVED';
    const isSelected = selectedTemplate?._id === template._id;

    return (
      <div
        key={template._id}
        onClick={() => isApproved && setSelectedTemplate(template)}
        style={{
          padding: '12px 16px',
          marginBottom: '8px',
          borderRadius: '8px',
          border: isSelected ? '2px solid #1890ff' : '1px solid #e8e8e8',
          backgroundColor: isSelected ? '#e6f7ff' : (isApproved ? '#fff' : '#f5f5f5'),
          cursor: isApproved ? 'pointer' : 'not-allowed',
          opacity: isApproved ? 1 : 0.6,
          transition: 'all 0.2s ease'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontWeight: 600, fontSize: '14px' }}>{template.name}</span>
            <Badge
              status={isApproved ? 'success' : 'warning'}
              text={template.status}
              style={{ fontSize: '11px' }}
            />
          </div>
          {isSelected && (
            <Check size={18} color="#1890ff" />
          )}
        </div>

        <div style={{
          fontSize: '13px',
          color: '#666',
          lineHeight: '1.4',
          maxHeight: '40px',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {getTemplateBody(template)}
        </div>

        {template.category && (
          <div style={{
            marginTop: '8px',
            fontSize: '11px',
            color: '#999'
          }}>
            {template.category} • {template.language || 'en'}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ul className="popup-section-4-left-1 mt-1 text-center">
        <li>
          {isPhoneValid ? (
            <a href={phoneLink} id="phoneTooltip">
              <span className="link_icon" style={{ backgroundColor: "#0EA5E9" }}>
                <Phone size={18} />
              </span>
            </a>
          ) : (
            <span className="link_icon" style={{ backgroundColor: "#9CA3AF" }} id="phoneTooltip">
              <Phone size={16} />
            </span>
          )}
          <Tooltip
            isOpen={tooltipOpen.phone}
            target="phoneTooltip"
            toggle={() => toggle('phone')}
          >
            Call Lead
          </Tooltip>
        </li>

        <li>
          {isEmailValid ? (
            <a href={emailLink} id="emailTooltip">
              <span className="link_icon" style={{ backgroundColor: "#EAB308" }}>
                <Mail size={16} />
              </span>
            </a>
          ) : (
            <span className="link_icon" style={{ backgroundColor: "#adadad" }} id="emailTooltip">
              <Mail size={16} />
            </span>
          )}
          <Tooltip
            isOpen={tooltipOpen.email}
            target="emailTooltip"
            toggle={() => toggle('email')}
          >
            Send Email
          </Tooltip>
        </li>

        <li>
          {isPhoneValid ? (
            <button
              onClick={handleWhatsAppClick}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}
              id="whatsappTooltip"
            >
              <span className="link_icon" style={{ backgroundColor: "#22C55E" }}>
                <FaWhatsapp size={16} />
              </span>
            </button>
          ) : (
            <span className="link_icon" style={{ backgroundColor: "#adadad" }} id="whatsappTooltip">
              <FaWhatsapp size={16} />
            </span>
          )}
          <Tooltip
            isOpen={tooltipOpen.whatsapp}
            target="whatsappTooltip"
            toggle={() => toggle('whatsapp')}
          >
            {isWhatsappIntegrated ? 'Send WhatsApp Message' : 'Open WhatsApp'}
          </Tooltip>
        </li>

        <li>
          <a onClick={onFileClick} id="filesTooltip">
            <span className="link_icon" style={{ backgroundColor: "#EF4444" }}>
              <Paperclip size={16} />
            </span>
          </a>
          <Tooltip
            isOpen={tooltipOpen.files}
            target="filesTooltip"
            toggle={() => toggle('files')}
          >
            Manage Files
          </Tooltip>
        </li>

        <li>
          <a onClick={onEditClick} id="editTooltip">
            <span className="link_icon" style={{ backgroundColor: "#3B82F6" }}>
              <Edit size={16} />
            </span>
          </a>
          <Tooltip
            isOpen={tooltipOpen.edit}
            target="editTooltip"
            toggle={() => toggle('edit')}
          >
            Edit Lead
          </Tooltip>
        </li>

        <li>
          {hasCustomForms ? (
            <a onClick={onFormResponseClick} id="externalTooltip">
              <span className="link_icon" style={{ backgroundColor: "#F87171" }}>
                <ExternalLink size={16} />
              </span>
            </a>
          ) : (
            <span className="link_icon" style={{ backgroundColor: "#adadad" }} id="externalTooltip">
              <ExternalLink size={16} />
            </span>
          )}
          <Tooltip
            isOpen={tooltipOpen.external}
            target="externalTooltip"
            toggle={() => toggle('external')}
          >
            View Form Response
          </Tooltip>
        </li>

        <li>
          <a onClick={handleCreateQuotation} id="quotationTooltip">
            <span className="link_icon" style={{ backgroundColor: "#8B5CF6" }}>
              <FileText size={16} />
            </span>
          </a>
          <Tooltip
            isOpen={tooltipOpen.quotation}
            target="quotationTooltip"
            toggle={() => toggle('quotation')}
          >
            Create Quotation
          </Tooltip>
        </li>
      </ul>

      {/* Choice Popup: WhatsApp App vs WhatsApp API */}
      <PopupComponent
        isOpen={showChoicePopup}
        onClose={() => setShowChoicePopup(false)}
        title="Send WhatsApp Message"
        size="sm"
      >
        <div style={{ padding: '8px 0' }}>
          <p style={{ marginBottom: '16px', color: '#666', fontSize: '14px' }}>
            How would you like to send a message to <strong>{leadName}</strong>?
          </p>

          {/* WhatsApp App Option */}
          <div
            onClick={handleChooseWhatsAppApp}
            style={{
              padding: '16px',
              marginBottom: '12px',
              borderRadius: '8px',
              border: '1px solid #e8e8e8',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#25d366';
              e.currentTarget.style.backgroundColor = '#f6ffed';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e8e8e8';
              e.currentTarget.style.backgroundColor = '#fff';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#25d366',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FaWhatsapp size={24} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>
                WhatsApp App
              </div>
              <div style={{ fontSize: '13px', color: '#888' }}>
                Opens WhatsApp Web / Desktop app
              </div>
            </div>
          </div>

          {/* WhatsApp API Option */}
          <div
            onClick={handleChooseWhatsAppAPI}
            style={{
              padding: '16px',
              borderRadius: '8px',
              border: '1px solid #e8e8e8',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#1890ff';
              e.currentTarget.style.backgroundColor = '#e6f7ff';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#e8e8e8';
              e.currentTarget.style.backgroundColor = '#fff';
            }}
          >
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#1890ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <MessageSquare size={24} color="#fff" />
            </div>
            <div>
              <div style={{ fontWeight: 600, fontSize: '15px', marginBottom: '4px' }}>
                WhatsApp API
              </div>
              <div style={{ fontSize: '13px', color: '#888' }}>
                Send template message via Business API
              </div>
            </div>
          </div>
        </div>
      </PopupComponent>

      {/* Template Selection Popup */}
      <PopupComponent
        isOpen={showTemplatePopup}
        onClose={handleCloseTemplatePopup}
        title="Select Template"
        size="md"
      >
        <div>
          {/* Lead info */}
          <div style={{
            padding: '12px 16px',
            backgroundColor: '#f6ffed',
            borderRadius: '8px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div>
              <div style={{ fontSize: '12px', color: '#52c41a', marginBottom: '2px' }}>
                Sending to:
              </div>
              <div style={{ fontWeight: 600 }}>
                {leadName}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {phone}
              </div>
            </div>
          </div>

          {/* Info about templates */}
          <div style={{
            padding: '10px 12px',
            backgroundColor: '#fff7e6',
            borderRadius: '6px',
            marginBottom: '16px',
            fontSize: '12px',
            color: '#d46b08',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px'
          }}>
            <MessageSquare size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
            <span>
              Select an <strong>approved template</strong> to send via WhatsApp Business API.
            </span>
          </div>

          {/* Template search */}
          <div style={{ marginBottom: '12px' }}>
            <Input
              placeholder="Search templates..."
              value={templateSearchTerm}
              onChange={(e) => setTemplateSearchTerm(e.target.value)}
              allowClear
            />
          </div>

          {/* Template list */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            <Spin spinning={loadingTemplates}>
              {filteredTemplates.length > 0 ? (
                filteredTemplates.map(template => renderTemplateItem(template))
              ) : loadingTemplates ? null : (
                <Empty
                  description="No templates found"
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                />
              )}
            </Spin>
          </div>

          {/* Send button */}
          <div style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #f0f0f0' }}>
            <Button
              type="primary"
              block
              size="large"
              onClick={handleSendTemplate}
              loading={sendingTemplate}
              disabled={!selectedTemplate || selectedTemplate.status !== 'APPROVED'}
              style={{ height: '44px' }}
            >
              {sendingTemplate
                ? 'Sending Template...'
                : selectedTemplate
                  ? `Send "${selectedTemplate.name}"`
                  : 'Select a Template'
              }
            </Button>
          </div>
        </div>
      </PopupComponent>
    </>
  );
};

// Wrap the component with React.memo to prevent unnecessary re-renders
export default React.memo(LeadActionIcons);
